import { createSelector } from 'reselect';
import { selectListAccountsFromConnections, selectSecondaryAddresses, selectUserAdditionalInfo, } from 'reducers/selectors';
import { EMPLOYMENT_STATUS, toStoredMonth } from './models';
export const selectMonevoOnboarding = (state) => state.monevoOnboarding;
export const selectMonevoLoanType = (state) => state.monevoOnboarding.type;
export const selectMonevoPreviousAddress = createSelector(selectSecondaryAddresses, (state) => state.monevoOnboarding.selectedPreviousAddressId, (secondaryAddresses, selectedId) => secondaryAddresses.find((address) => address.id === selectedId));
/**
 * Monevo is given a sort code and account number, so only a live GBP current
 * account we hold both for, and still hold a connection to, can be submitted.
 */
const isEligibleAccount = (account) => account.type === 'CHECKING' &&
    !!account.sortCode &&
    !!account.accountNumber &&
    account.provider !== 'MANUAL' &&
    account.currency === 'GBP' &&
    !account.isRemoved &&
    !account.isClosed &&
    !account.isConnectionClosed;
export const selectMonevoEligibleAccounts = createSelector(selectListAccountsFromConnections, (accounts) => accounts.filter(isEligibleAccount));
/**
 * The account the flow works from unless the user picks another — the one
 * saved on the user, else the first eligible one.
 */
export const selectMonevoMainBankAccount = createSelector(selectMonevoEligibleAccounts, (state) => selectUserAdditionalInfo(state).mainBankAccountId, (accounts, savedId) => accounts.find((account) => account.id === savedId) ?? accounts.at(0));
/**
 * The account the flow is working from: the one picked in this run, else the
 * one saved on the user, else the first eligible one.
 */
export const selectMonevoSelectedAccount = createSelector(selectMonevoEligibleAccounts, selectMonevoMainBankAccount, (state) => state.monevoOnboarding.mainBankAccountId, (accounts, defaultAccount, pickedId) => accounts.find((account) => account.id === pickedId) ?? defaultAccount);
/**
 * What the backend already holds, as flow answers. Seeded on the way in so
 * the screens have one source to read and write, rather than falling back to
 * the user record field by field.
 */
export const selectMonevoSeedFromUser = (state) => {
    const info = selectUserAdditionalInfo(state);
    return {
        income: info.grossAnnualSalary
            ? Math.floor(info.grossAnnualSalary)
            : undefined,
        employmentStatus: EMPLOYMENT_STATUS.find((status) => status.incomeSource === info.incomeSource),
        employmentStartDate: toStoredMonth(info.employmentStartDate),
        maritalStatus: info.maritalStatus,
        // Nobody is asked to enter a zero, so an unanswered count reads as none.
        dependantNumber: info.dependantNumber ?? 0,
        rentalMortgagePayments: info.rentMortgagePayment ?? undefined,
        timeAtBank: info.yearsAtBank == null ? undefined : String(info.yearsAtBank),
        mainBankAccountId: selectMonevoMainBankAccount(state)?.id,
    };
};
/**
 * Everything the flow collected that belongs on the user, minus what the
 * backend already holds. Saved in one request on the last screen, right
 * before the quote is asked for — the backend reads some of these off the
 * user rather than out of the quote body, so the write has to land first.
 */
export const selectMonevoAdditionalInfoChanges = (state) => {
    const onboarding = selectMonevoOnboarding(state);
    const info = selectUserAdditionalInfo(state);
    const changes = {};
    const income = onboarding.income == null ? undefined : Math.floor(onboarding.income);
    if (income != null && income !== info.grossAnnualSalary) {
        changes.grossAnnualSalary = income;
    }
    const incomeSource = onboarding.employmentStatus?.incomeSource;
    if (incomeSource && incomeSource !== info.incomeSource) {
        changes.incomeSource = incomeSource;
    }
    const startDate = toStoredMonth(onboarding.employmentStartDate);
    if (startDate && startDate !== toStoredMonth(info.employmentStartDate)) {
        changes.employmentStartDate = startDate;
    }
    else if (onboarding.employmentStartDate === null &&
        info.employmentStartDate != null) {
        changes.employmentStartDate = null;
    }
    if (onboarding.maritalStatus &&
        onboarding.maritalStatus !== info.maritalStatus) {
        changes.maritalStatus = onboarding.maritalStatus;
    }
    if (onboarding.dependantNumber != null &&
        onboarding.dependantNumber !== info.dependantNumber) {
        changes.dependantNumber = onboarding.dependantNumber;
    }
    if (onboarding.rentalMortgagePayments != null &&
        onboarding.rentalMortgagePayments !== info.rentMortgagePayment) {
        changes.rentMortgagePayment = onboarding.rentalMortgagePayments;
    }
    const yearsAtBank = Number(onboarding.timeAtBank);
    if (onboarding.timeAtBank && yearsAtBank !== info.yearsAtBank) {
        changes.yearsAtBank = yearsAtBank;
    }
    if (onboarding.mainBankAccountId != null &&
        onboarding.mainBankAccountId !== info.mainBankAccountId) {
        changes.mainBankAccountId = onboarding.mainBankAccountId;
    }
    return changes;
};
